import jsPDF from "jspdf";
import { query, getDocs, orderBy, limit } from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../utils/reports/pdfChrome";
import { downloadBlob } from "../utils/reports/downloadBlob";
import { buildReportFilename } from "../utils/reports/reportFilename";

const GenerateDRListPDF = async (
  clientId,
  selectedProperty,
  rooms,
  setStatusMessage,
  user,
  fileName
) => {
  setStatusMessage("Initializing PDF");
  const pdf = new jsPDF({
    orientation: "portrait", // or 'landscape' depending on your needs
    unit: "mm",
    format: [280, 297], //### Width increased by 70mm to accommodate more content
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 5;
  const maxLineWidth = pageWidth - margin * 2; // Define the maximum width for the text

  const leftAlign = (text, ypos) => {
    const wrappedText = pdf.splitTextToSize(text, maxLineWidth); // Automatically wrap text
    pdf.text(wrappedText, margin, ypos);
  };

  const centerAlign = (text, ypos) => {
    const textWidth = pdf.getTextWidth(text);
    const xpos = (pageWidth - textWidth) / 2;

    pdf.text(text, xpos, ypos);
  };

  const contentTop = drawReportHeader(pdf, {
    title: fileName || "Down Rooms List",
    generatedBy: user?.fullName || "Unknown",
  });

  const downRooms = rooms.filter(
    (room) => room.status === "Down" || room.status === "Facilities"
  );

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(12);
  centerAlign(`including units`, contentTop + 6);
  leftAlign(
    `${downRooms.map((room) => room.displayName).join(", ")}`,
    contentTop + 12
  );

  centerAlign(`total items needed:`, 100);

  //end of front page
  pdf.addPage();

  setStatusMessage(`Generating PDF with ${downRooms.length} rooms`);

  let itemsNeeded = [];

  for (const room of downRooms) {
    let unitSpecificItemsNeeded = [];
    //add room needs to itemsNeeded

    //for each key starting with itemCount, if the value of the key is greater than 0, add the key and value to itemsNeeded
    Object.keys(room).forEach((key) => {
      if (key.includes("itemCount") && room[key] > 0) {
        // if item not in itemsNeeded array Add the item and count to the itemsNeeded array, else, add the count to the existing item in the itemsNeeded array
        const itemIndex = itemsNeeded.findIndex(
          (item) => item.item === key.replace("itemCount", "")
        );
        if (itemIndex !== -1) {
          itemsNeeded[itemIndex].count += room[key];
        } else {
          itemsNeeded.push({
            item: key.replace("itemCount", ""),
            count: room[key],
          });
        }
        unitSpecificItemsNeeded.push({
          item: key.replace("itemCount", ""),
          count: room[key],
        });
      }
    });

    // Add room number in bold at the top of the page
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text(room.displayName, 10, 20);
    if (room.unitNotes) {
      pdf.setFontSize(10); ////##### Changed to 10 from 12
      leftAlign(`Notes: ${room.unitNotes}`, 30);
    }

    // Fetch images for the room
    // #########ORIGINAL##############
    // setStatusMessage(`Fetching images for ${room.displayName}`);
    // const imagesRef = collection(
    //   firestore,
    //   "properties",
    //   selectedProperty,
    //   "units",
    //   room.id,
    //   "images"
    // );

    // const imagesQuerySnapshot = await getDocs(
    //   query(imagesRef, orderBy("uploadedAt", "desc"), limit(6))
    // );
    // let imageRow = 50; // Initial position for the first image//##### Changed to 50 from 30
    // let imageCol = 10; // Initial position for the first image column
    // let imagesCount = 0; // Counter for images per page

    // for (const doc of imagesQuerySnapshot.docs) {
    //   setStatusMessage(`${room.displayName}: Adding image ${imagesCount + 1}`);
    //   if (imagesCount >= 6) break; // Stop if six images have been added

    //   const image = doc.data();

    //   try {
    //     const response = await fetch(image.url);
    //     const blob = await response.blob();
    //     const img = new Image();
    //     const url = URL.createObjectURL(blob);
    //     img.src = url;

    //     await new Promise((resolve) => {
    //       img.onload = () => {
    //         pdf.addImage(img, "JPEG", imageCol, imageRow, 80, 80); // Adjust the size as needed
    //         URL.revokeObjectURL(url);
    //         pdf.link(imageCol, imageRow, 80, 80, {
    //           ///##### Added link to the image
    //           url: image.url,
    //         });
    //         imageCol += 90; // Adjust the gap between images as needed

    //         // Move to next row if two images are in the current row////##### Changed to threee images per row
    //         if (imageCol > 190) {
    //           imageCol = 10;
    //           imageRow += 90;
    //         }

    //         imagesCount += 1;
    //         resolve();
    //       };
    //     });
    //   } catch (error) {
    //     pdf.setFontSize(12);
    //     pdf.setFont("helvetica", "normal");
    //     pdf.text("Issue getting image", imageCol, imageRow + 40);

    //     imageCol += 90; // Adjust the gap between images as needed

    //     // Move to next row if two images are in the current row ////##### Changed to threee images per row
    //     if (imageCol > 190) {
    //       imageCol = 10;
    //       imageRow += 90;
    //     }

    //     imagesCount += 1;
    //   }
    // }

    // Fetch images for the room (parallelized)
    setStatusMessage(`Fetching images for ${room.displayName}`);
    const imagesRef = propertyCollection(
      clientId,
      selectedProperty,
      "units",
      room.id,
      "images"
    );

    const imagesQuerySnapshot = await getDocs(
      query(imagesRef, orderBy("uploadedAt", "desc"), limit(6))
    );

    // Prepare up to 6 image loaders in original order
    const docs = imagesQuerySnapshot.docs.slice(0, 6);

    // helper: load an image via fetch -> blob -> objectURL -> Image()
    const loadImage = (url) =>
      new Promise(async (resolve, reject) => {
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const objUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => resolve({ img, objUrl });
          img.onerror = (e) => reject(new Error("Image decode failed"));
          img.src = objUrl;
        } catch (err) {
          reject(err);
        }
      });

    // Kick off all loads in parallel, preserving index for placement
    const loaders = docs.map((doc, idx) => {
      const { url } = doc.data();
      return loadImage(url)
        .then(({ img, objUrl }) => ({
          status: "fulfilled",
          idx,
          img,
          objUrl,
          url,
        }))
        .catch((error) => ({ status: "rejected", idx, error, url }));
    });

    setStatusMessage(
      `${room.displayName}: Loading ${docs.length} image(s) in parallel`
    );
    const results = await Promise.allSettled(loaders);

    // Placement constants (same sizing/layout as before)
    const startX = 10;
    const startY = 50; // images grid top
    const tileW = 80;
    const tileH = 80;
    const gap = 90; // step between tiles
    const perRow = 3;

    // Iterate in index order so visual placement matches query order
    for (let i = 0; i < results.length; i++) {
      const r =
        results[i].status === "fulfilled"
          ? results[i].value
          : results[i].reason || results[i];
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const x = startX + col * gap;
      const y = startY + row * gap;

      if (r.status === "fulfilled" && r.img) {
        try {
          pdf.addImage(r.img, "JPEG", x, y, tileW, tileH);
          if (r.objUrl) URL.revokeObjectURL(r.objUrl);
          if (r.url) {
            pdf.link(x, y, tileW, tileH, { url: r.url });
          }
        } catch (e) {
          // Fallback text on addImage failure
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "normal");
          pdf.text("Issue adding image", x, y + tileH / 2);
          if (r.objUrl) URL.revokeObjectURL(r.objUrl);
        }
      } else {
        // Rejected load -> write an issue marker at the tile position
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        pdf.text("Issue getting image", x, y + tileH / 2);
      }
    }

    ////###added unit specific items to the pdf
    if (unitSpecificItemsNeeded) {
      pdf.setFontSize(10); ////##### Changed to 10 from 12
      leftAlign(
        `Items Needed: ${unitSpecificItemsNeeded
          .sort((a, b) => a.item.localeCompare(b.item))
          .map((item) => `${item.item}: ${item.count}`)
          .join(", ")}`,
        43
      );
    }

    if (downRooms.indexOf(room) !== downRooms.length - 1) {
      pdf.addPage(); // Add new page if not the last room
    }
  }
  pdf.setPage(1);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(12);
  leftAlign(
    itemsNeeded
      .sort((a, b) => a.item.localeCompare(b.item))
      .map((item) => `${item.item}: ${item.count}`)
      .join(", "),
    125
  );

  setStatusMessage("Finishing up PDF");

  stampPageNumbers(pdf);

  const pdfBlob = pdf.output("blob");
  downloadBlob(pdfBlob, buildReportFilename("Generated_DRL", { ext: "pdf" }));
};

export default GenerateDRListPDF;
